import React from "react";

type TableSkeletonProps = {
  /** How many placeholder rows to draw. */
  rows?: number;
  /** Columns the real table has, so the skeleton spans the full width. */
  colSpan?: number;
};

/**
 * Placeholder rows for a desktop table that is still fetching its first page.
 * Rendered inside <tbody> so the header and column widths stay put.
 */
export function TableSkeleton({ rows = 6, colSpan = 6 }: TableSkeletonProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i}>
          <td colSpan={colSpan} className="p-0">
            <div
              className="h-14 animate-pulse bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 dark:from-slate-700 dark:via-slate-800 dark:to-slate-700"
              style={{ animationDelay: `${i * 80}ms` }}
            />
          </td>
        </tr>
      ))}
    </>
  );
}

type CardSkeletonProps = {
  /** How many placeholder cards to draw. */
  cards?: number;
  /** Card height, matching the real card on that page. */
  height?: string;
};

/** Placeholder cards for the mobile view of a list that is still loading. */
export function CardSkeleton({ cards = 3, height = "h-36" }: CardSkeletonProps) {
  return (
    <>
      {Array.from({ length: cards }).map((_, i) => (
        <div
          key={i}
          className={`${height} rounded-xl border border-slate-200 dark:border-slate-600 animate-pulse bg-slate-100 dark:bg-slate-700/50`}
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </>
  );
}

/**
 * Overlay version for tables that cannot host extra <tr>s, e.g. a wrapper that
 * already positions its own absolute children.
 */
export function TableSkeletonOverlay({ rows = 6 }: { rows?: number }) {
  return (
    <div className="absolute inset-0 bg-white/60 dark:bg-slate-800/60 backdrop-blur-[1px] flex flex-col gap-0">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-14 border-b border-slate-100 dark:border-slate-700/80 animate-pulse bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 dark:from-slate-700 dark:via-slate-800 dark:to-slate-700"
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
  );
}

/** Placeholder block for list screens that render panels rather than a table. */
export function BlockSkeleton({ blocks = 3, height = "h-20" }: { blocks?: number; height?: string }) {
  return (
    <>
      {Array.from({ length: blocks }).map((_, i) => (
        <div
          key={i}
          className={`${height} rounded-lg animate-pulse bg-slate-100 dark:bg-slate-700/50`}
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </>
  );
}
